/**
 * OAuth service — wraps auth.oauth_start / auth.oauth_finish RPC calls and
 * manages the temporary browser-side state required by the OAuth 2.0
 * authorization-code flow.
 *
 * Session storage keys used (cleared after every terminal outcome):
 *   oauth_provider  — 'google' | 'microsoft'
 *   oauth_state     — opaque state string from auth.oauth_start
 */

import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { safeSessionStorage } from '@/utils/safeStorage';

/** @typedef {'google' | 'microsoft' | 'github'} OAuthProvider */

/**
 * @typedef {Object} OAuthStartResult
 * @property {OAuthProvider} provider
 * @property {string}        auth_url
 * @property {string}        state
 */

/**
 * @typedef {Object} OAuthFinishResult
 * @property {string}   username
 * @property {string}   firstname
 * @property {string}   lastname
 * @property {string}   token
 * @property {string[]} roles
 * @property {boolean}  changepassword
 * @property {boolean}  require2fa
 */

const STORAGE_KEY_PROVIDER = 'oauth_provider';
const STORAGE_KEY_STATE    = 'oauth_state';

/**
 * Persist the oauth handshake values so they survive the redirect round-trip.
 * @param {OAuthProvider} provider
 * @param {string}        state
 */
function saveOAuthHandshake(provider, state) {
  safeSessionStorage.setItem(STORAGE_KEY_PROVIDER, provider);
  safeSessionStorage.setItem(STORAGE_KEY_STATE, state);
}

/**
 * Read the values that were persisted before the redirect.
 * @returns {{ provider: string | null, state: string | null }}
 */
export function readOAuthHandshake() {
  return {
    provider: safeSessionStorage.getItem(STORAGE_KEY_PROVIDER),
    state:    safeSessionStorage.getItem(STORAGE_KEY_STATE),
  };
}

/**
 * Remove temporary oauth keys from session storage.
 * Should be called after every terminal outcome (success or unrecoverable error).
 */
export function clearOAuthHandshake() {
  safeSessionStorage.removeItem(STORAGE_KEY_PROVIDER);
  safeSessionStorage.removeItem(STORAGE_KEY_STATE);
}

/**
 * Kick off the OAuth flow for the given provider:
 *   1. Calls auth.oauth_start to get the provider authorization URL + state.
 *   2. Persists provider + state in session storage.
 *   3. Redirects the browser to auth_url.
 *
 * @param {OAuthProvider} provider
 * @returns {Promise<never>} Resolves only if the redirect fires; rejects on RPC error.
 */
export async function startSocialLogin(provider) {
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.oauthStart({ provider });

  if (resp.error) {
    throw resp.error;
  }
  if (!resp.result || !resp.result.auth_url) {
    throw new Error('Invalid response from auth.oauth_start');
  }

  /** @type {OAuthStartResult} */
  const result = resp.result;
  saveOAuthHandshake(result.provider || provider, result.state);
  window.location.href = result.auth_url;
}

/**
 * Complete the OAuth flow after the provider redirects back to the callback URL.
 *   1. Validates state matches the stored value (CSRF guard).
 *   2. Calls auth.oauth_finish with provider, code, state.
 *   3. Returns the same auth payload shape as auth.login.
 *
 * @param {OAuthProvider} provider
 * @param {string}        code    — authorization code from the callback URL
 * @param {string}        state   — state from the callback URL
 * @returns {Promise<OAuthFinishResult>}
 */
export async function finishSocialLogin(provider, code, state) {
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.oauthFinish({ provider, code, state });

  if (resp.error) {
    throw resp.error;
  }
  if (!resp.result) {
    throw new Error('Empty response from auth.oauth_finish');
  }

  return /** @type {OAuthFinishResult} */ (resp.result);
}
